import { timestampDate } from '@bufbuild/protobuf/wkt';
import {
  ListActionsAndIssuesGraphResponse_IssueLane,
  ListActionsAndIssuesGraphResponse_Issue,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import {
  XrangePointOptionsObject,
  SeriesXrangeOptions,
  YAxisOptions,
} from 'highcharts';
import { TFunction } from 'i18next';
import { dateToLocaleString } from '~/pages/Metrics/date-to-locale-string';
import { ACTION_LANE_WIDTH } from '~/pages/Metrics/models/ActionsChartModel';
import { issueShortLabel } from '~/shared/models/issues-conversions';
import { theme } from '~/shared/theme/theme';

export class IssuesChartModel {
  lanes = 0;

  chartData: XrangePointOptionsObject[] = [];

  chartHeight = 0;

  constructor(issueLanes: ListActionsAndIssuesGraphResponse_IssueLane[]) {
    this.lanes = issueLanes.length;

    this.chartData = issueLanes
      .map((lane, index) =>
        lane.issues.map((issue) => ({
          // Index has to be added to x and x2 because otherwise Highcharts treats
          // the ranges with same x1 and x2 as the same and tooltips don't work as intended
          x:
            (issue.firstOccurrenceTime
              ? timestampDate(issue.firstOccurrenceTime).getTime()
              : 0) + index,
          x2:
            (issue.closedAtTime
              ? timestampDate(issue.closedAtTime).getTime()
              : 0) + index,
          y: 0,
          color: `${theme.palette.error.main}4D`,
          custom: {
            issue,
          },
        })),
      )
      .flat();

    this.chartHeight = this.chartData.length ? 2.5 * ACTION_LANE_WIDTH : 0;
  }

  createSeries(t: TFunction, yIndex: number): SeriesXrangeOptions {
    return {
      type: 'xrange',
      data: this.chartData,
      minPointLength: 3,
      yAxis: yIndex,
      pointWidth: ACTION_LANE_WIDTH,
      borderRadius: 0,
      showInNavigator: false,
      enableMouseTracking: false,
      states: {
        inactive: {
          opacity: 1,
        },
      },
      groupPadding: 0.5,
    };
  }

  createIssuesYAxis = (t: TFunction): YAxisOptions => {
    if (!this.chartData.length) {
      return {
        height: this.chartHeight,
        opposite: false,
        labels: {
          enabled: false,
        },
        categories: [],
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      };
    }

    return {
      height: this.chartHeight,
      opposite: false,
      title: {
        rotation: 0,
        reserveSpace: false,
        textAlign: 'right',
        x: -10,
        y: 5,
        style: {
          color: theme.palette.primary.dark,
          fontSize: '14px',
          lineHeight: '14px',
        },
        text: t('issues.title'),
      },
      labels: {
        enabled: false,
      },
      categories: [t('issues.title')],
    };
  };

  public static getChartTooltip({
    issue,
    t,
  }: {
    issue: ListActionsAndIssuesGraphResponse_Issue;
    t: TFunction;
  }): string {
    const startDate =
      issue.firstOccurrenceTime && timestampDate(issue.firstOccurrenceTime);
    const endDate = issue.closedAtTime && timestampDate(issue.closedAtTime);

    return `
      <div>
      <strong>${issueShortLabel(t, issue.issueType)}</strong> - ${
        issue.resourceTitle
      }
      <br />
      <span>🕒 ${dateToLocaleString(startDate)} → ${dateToLocaleString(
        endDate,
      )}</span>
      </div>
    `;
  }
}
